import { useEffect, useLayoutEffect, useState } from "react";
import { isSafari } from "react-device-detect";
import { Flex, Text, useToast } from "@chakra-ui/react";
import { Squiggle170x10Icon } from "../../components/icons/Squiggle170x10Icon";
import { FUNNEL_TYPES } from "../../components/funnel/utils/contants";
import {
  FunnelContextArea,
  EventContextArea,
} from "../../components/funnel/types";
import { useGetNavigationContext } from "../../hooks/useGetNavigationContext";
import { useAccountData } from "../../hooks/useAccountData";
import { useContentFunnel } from "../../components/funnel/hooks/useContentFunnel";
import { useMobileBreakpoint } from "../../utils/useBreakpoints";
import { t } from "i18next";
import { ValkyraeFooter } from "../valkyrae/ValkyraeFooter";
import { useNavigate } from "react-router";
import { setFunnelModalConfig } from "../../components/funnel/FunnelModal";
import { Trans } from "react-i18next";
import { HolidayBundleHero } from "./HolidayBundleHero";
import { YearlyBundleReleasesGrid } from "../../components/cards/YearlyBundleReleasesGrid";
import { HolidayBundlePublisherLogos } from "./components/HolidayBundlePublisherLogos";

export function HolidayBundleScreen() {
  const isMobileBreakpoint = useMobileBreakpoint();
  const accountData = useAccountData();
  const navigate = useNavigate();
  const [activeFunnel, setActiveFunnel] = useState<FUNNEL_TYPES | undefined>(
    FUNNEL_TYPES.subscription,
  );

  useLayoutEffect(() => {
    if (accountData && !accountData.features[`holiday_bundle`]) {
      navigate("/", { replace: true });
    }
  }, [accountData]);

  const focusPayment = () => {
    const container = document.querySelector(
      "[data-payment-section]",
    ) as HTMLElement;

    if (!container) {
      console.warn("Payment container element not found.");
      return; // Exit if the element isn't there
    }

    if (isSafari) {
      // Apply a longer delay for better reliability on iOS/Safari
      setTimeout(() => {
        // Use block: "start" or "nearest" which are often more reliable on mobile.
        // block: "nearest" is a good default.
        container.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          // inline: "nearest" is fine but often redundant for vertical scrolling
        });
      }, 500); // Increased timeout to 500ms
    } else {
      // Standard behavior for other browsers
      container.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  };

  return (
    <Flex direction="column" width="100%">
      <Flex
        as="main"
        role="main"
        position="relative"
        width="100%"
        flexDirection="column"
        paddingBottom="40px"
        alignItems="center"
        backgroundImage={`url(${window.gc.global.assets_url}/static/images/web-ui/black-friday/hero_glow_black_friday.svg)`}
        backgroundRepeat="no-repeat"
        backgroundPosition={isMobileBreakpoint ? "center -23%" : "center -33px"}
        overflowX="hidden"
      >
        <HolidayBundleHero onGetDealClick={focusPayment} />
        <Flex
          flexDirection="column"
          width="100%"
          maxWidth="964px"
          alignItems="center"
        >
          <Flex
            flexDirection="column"
            width="100%"
            alignItems="center"
            gap="24px"
          >
            {/* Paywall Section */}
            {isMobileBreakpoint && (
              <Flex
                width="100%"
                px={isMobileBreakpoint ? "20px" : "0"}
                pb="32px"
              >
                <Flex
                  data-payment-section
                  padding={isMobileBreakpoint ? "32px 24px" : "64px 32px"}
                  background="charcoal.700"
                  borderRadius="24px"
                  width="100%"
                  justifyContent="center"
                  mt="-100px"
                >
                  <Flex maxWidth="512px" width="100%" justifyContent="center">
                    <HolidayBundlePayment
                      contextArea={FunnelContextArea.holidayBundle}
                      title={t("screens.holidayBundle.becomeAGoldMember")}
                      activeFunnel={activeFunnel}
                      setActiveFunnel={setActiveFunnel}
                    />
                  </Flex>
                </Flex>
              </Flex>
            )}

            <Text
              color="dune.dune"
              textAlign="center"
              fontFamily="Bricolage Grotesque"
              fontSize={isMobileBreakpoint ? "32px" : "40px"}
              fontWeight="800"
              lineHeight="107%"
              marginTop={isMobileBreakpoint ? undefined : "-30px"}
              px={isMobileBreakpoint ? "20px" : "0"}
            >
              <Trans
                i18nKey="screens.holidayBundle.theBestComicSubscription"
                components={{ 1: <br /> }}
              />
            </Text>
            <Squiggle170x10Icon
              width="170px"
              height="10px"
              color="blaze.blaze"
            />
            <Text
              color="dune.dune"
              textAlign="center"
              fontSize="18px"
              maxWidth="800px"
              fontWeight="500"
              px="60px"
            >
              <Trans
                i18nKey="screens.holidayBundle.gcAlreadyGivesYouUnlimitedAccess"
                components={{ 1: <br /> }}
              />
            </Text>
          </Flex>

          <HolidayBundlePublisherLogos />

          {/* Release Cards Grid */}
          <Flex
            flexDirection="column"
            width="100%"
            alignItems="center"
            mt="40px"
            mb="32px"
          >
            <YearlyBundleReleasesGrid
              sectionKeyProd="1ce24fc7-c227-44ab-997d-4479b61f28eb"
              sectionKeyDev="6048f1fb-0243-4578-9fbe-313791242427"
              queryKeyName="holidayBundleReleases"
            />
          </Flex>

          {/* Paywall Section */}
          {!isMobileBreakpoint && (
            <Flex width="100%" px={isMobileBreakpoint ? "20px" : "0"} pb="32px">
              <Flex
                data-payment-section
                padding={isMobileBreakpoint ? "32px 24px" : "64px 32px"}
                background="charcoal.700"
                borderRadius="24px"
                width="100%"
                justifyContent="center"
                mt="40px"
              >
                <Flex maxWidth="512px" width="100%" justifyContent="center">
                  <HolidayBundlePayment
                    contextArea={FunnelContextArea.holidayBundle}
                    title={t("screens.holidayBundle.becomeAGoldMember")}
                    activeFunnel={activeFunnel}
                    setActiveFunnel={setActiveFunnel}
                  />
                </Flex>
              </Flex>
            </Flex>
          )}

          <ValkyraeFooter isMobileBreakpoint={isMobileBreakpoint} />
        </Flex>
      </Flex>
    </Flex>
  );
}

export function HolidayBundlePayment({
  contextArea,
  title,
  activeFunnel,
  setActiveFunnel,
}: {
  contextArea: FunnelContextArea;
  title?: string | JSX.Element;
  activeFunnel: FUNNEL_TYPES | undefined;
  setActiveFunnel: (type: FUNNEL_TYPES | undefined) => void;
  isPurchaseMode?: boolean;
}) {
  const accountData = useAccountData();

  // Reset funnel when user logs out
  useEffect(() => {
    if (!accountData?.user) {
      setActiveFunnel(FUNNEL_TYPES.subscription);
    }
  }, [accountData?.user, setActiveFunnel]);

  const navigate = useNavigate();
  const createToast = useToast();
  const navigationContext = useGetNavigationContext({
    contextArea: EventContextArea.blackFridayPage,
  });

  const propsToPass = {
    displayExploreMoreContent: false,
    hideValueProps: true,
    customHeader: title,
    customSubmitButtonText: t("screens.holidayBundle.signUpNow"),
    centerWelcomeText: true,
    largeLoginCTA: true,
    hidePromoCode: true,
    showYearlyOnly: true,
  };

  const { funnelBody } = useContentFunnel({
    setFunnelConfig: (funnelConfig) => setActiveFunnel(funnelConfig?.type),
    onCloseFunnel: () => setActiveFunnel(undefined),
    openFunnelType: activeFunnel,
    context: { modalContext: contextArea, navigationContext },

    options: {
      componentProps: propsToPass,
      onSuccess: (result) => {
        setFunnelModalConfig({
          type: FUNNEL_TYPES.holidayBundleCongrats,
          context: result.context,
          options: {
            ...result.options,
            onSuccess: () => {
              setFunnelModalConfig();
            },
          },
        });
        navigate("/");
        createToast({
          status: "success",
          description: t("screens.gold.goldSubscriptionActivated"),
          duration: 5000,
        });
      },
    },
  });

  return funnelBody;
}
